import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";
import {
  FlowCreate,
  FlowUpdate,
  FlowResponse,
  FlowFilter,
  FlowTraceabilityResponse,
  FlowAttachedFile,
} from "../types/flow";
import APIFlowIdSub from "./flowId/client";

export default class APIFlows extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  flowId = new APIFlowIdSub(this._client);

  // Create a new flow
  create = async (flowData: FlowCreate): Promise<FlowResponse> => {
    let res = await this._fetch({
      url: `/flows/`,
      method: "POST",
      body: flowData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get all unique metadata keys from flows
  getMetadataKeys = async (): Promise<string[]> => {
    const res = await this._fetch({
      url: `/flows/metadata-keys`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flows list with filtering and pagination
  list = async (
    filter?: FlowFilter,
    page: number = 1,
    size: number = 50,
  ): Promise<types.PageFlowResponse> => {
    let params: Record<string, unknown> = {
      page,
      size,
    };
    if (filter) {
      if (filter.product_id) params.product_id = filter.product_id;
      if (filter.partner_id) params.partner_id = filter.partner_id;
      if (filter.stock_id) params.stock_id = filter.stock_id;
      if (filter.transaction_id) params.transaction_id = filter.transaction_id;
      if (filter.direction) params.direction = filter.direction;
      if (filter.flow_type) params.flow_type = filter.flow_type;
      if (typeof filter.provisional === "boolean")
        params.provisional = filter.provisional;
      if (filter.flow_types) params.flow_types = filter.flow_types;
      if (filter.created_at_s) params.created_at_s = filter.created_at_s;
      if (filter.created_at_e) params.created_at_e = filter.created_at_e;
      // Extended filters for deep search
      if (filter.search) params.search = filter.search;
      if (filter.ggn) params.ggn = filter.ggn;
      if (filter.lot_id) params.lot_id = filter.lot_id;
      if (filter.operation_id) params.operation_id = filter.operation_id;
      if (filter.location_id) params.location_id = filter.location_id;
    }

    let res = await this._fetch({
      url: `/flows/`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a single flow by ID
  get = async (flowId: UUID): Promise<FlowResponse> => {
    let res = await this._fetch({
      url: `/flows/${flowId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a flow
  update = async (
    flowId: UUID,
    flowData: FlowUpdate,
  ): Promise<FlowResponse> => {
    let res = await this._fetch({
      url: `/flows/${flowId}`,
      method: "PUT",
      body: flowData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Alias for update (for backward compatibility)
  putFlowId = this.update;

  // Delete a flow
  delete = async (flowId: UUID): Promise<void> => {
    await this._fetch({
      url: `/flows/${flowId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Duplicate a flow
  duplicate = async (flowId: UUID): Promise<FlowResponse> => {
    let res = await this._fetch({
      url: `/flows/${flowId}/duplicate`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flows by stock ID
  getByStock = async (stockId: UUID): Promise<FlowResponse[]> => {
    let res = await this._fetch({
      url: `/flows/stock/${stockId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flows by product ID
  getByProduct = async (productId: UUID): Promise<FlowResponse[]> => {
    let res = await this._fetch({
      url: `/flows/product/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flows by transaction ID
  getByTransaction = async (transactionId: UUID): Promise<FlowResponse[]> => {
    let res = await this._fetch({
      url: `/flows/transaction/${transactionId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flow value
  getValue = async (
    flowId: UUID,
  ): Promise<{ flow_id: UUID; value: number }> => {
    let res = await this._fetch({
      url: `/flows/${flowId}/value`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get flow traceability - bidirectional flow chain
  getTraceability = async (flowId: UUID): Promise<FlowTraceabilityResponse> => {
    let res = await this._fetch({
      url: `/flows/${flowId}/traceability`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Document management methods

  // Get documents for a flow
  getDocuments = async (
    flowId: UUID,
  ): Promise<{ flow_id: UUID; files: FlowAttachedFile[] }> => {
    const res = await this._fetch({
      url: `/flows/${flowId}/documents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Upload documents to a flow
  uploadDocuments = async (
    flowId: UUID,
    files: File[],
  ): Promise<{ message: string; files: FlowAttachedFile[]; flow_id: UUID }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const res = await this._fetch({
      url: `/flows/${flowId}/documents/upload`,
      method: "POST",
      body: formData,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Download a document from a flow
  downloadDocument = async (flowId: UUID, fileUrl: string): Promise<Blob> => {
    const res = await this._fetch({
      url: `/flows/${flowId}/documents/download?file_url=${encodeURIComponent(fileUrl)}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // Remove a document from a flow
  removeDocument = async (
    flowId: UUID,
    fileUrl: string,
  ): Promise<{
    message: string;
    removed_file: FlowAttachedFile;
    flow_id: UUID;
  }> => {
    const res = await this._fetch({
      url: `/flows/${flowId}/documents/remove?file_url=${encodeURIComponent(fileUrl)}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Rename a document in a flow
  renameDocument = async (
    flowId: UUID,
    fileUrl: string,
    newName: string,
  ): Promise<{ message: string; new_name: string; flow_id: UUID }> => {
    const res = await this._fetch({
      url: `/flows/${flowId}/documents/rename?file_url=${encodeURIComponent(fileUrl)}`,
      method: "PUT",
      body: { new_name: newName },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
